export type ConsentScope =
  | 'core_service'
  | 'ai_personalisation'
  | 'selected_reflections'
  | 'push_notifications'
  | 'offline_persistent_cache'

export type ConsentDecision = 'grant' | 'deny' | 'revoke'

export interface ConsentEvent {
  eventId: string
  ownerUid: string
  scope: ConsentScope
  decision: ConsentDecision
  granted: boolean
  noticeVersion: string
  policyVersion: string
  dataCategories: string[]
  processorCategories: string[]
  retentionChoice: string
  previousEventId: string | null
  occurredAt: string
  uiSource: 'privacy_centre' | 'point_of_collection'
}

export interface ConsentScopeState {
  granted: boolean
  lastEventId: string
  decidedAt: string
  noticeVersion: string
}

export interface ConsentState {
  version: number
  scopes: Partial<Record<ConsentScope, ConsentScopeState>>
}

export interface ConsentDecisionRequest {
  scope: ConsentScope
  decision: ConsentDecision
  previousEventId: string | null
  uiSource: ConsentEvent['uiSource']
}

export const consentScopeMeta: Record<ConsentScope, {
  title: string
  summary: string
  collectionNotice: string
  dataCategories: string[]
  processorCategories: string[]
  optional: boolean
}> = {
  core_service: {
    title: 'Core account and service',
    summary: 'Keeps your account, approved practices, responses and privacy choices working.',
    collectionNotice: 'Required while the account is active. Closing the account starts the deletion workflow.',
    dataCategories: ['account identity', 'practice state', 'privacy choices'],
    processorCategories: ['Firebase authentication and database'],
    optional: false
  },
  ai_personalisation: {
    title: 'PFD AI personalisation',
    summary: 'Allows selected, labelled facts to be included in coach-triggered PFD intelligence runs.',
    collectionNotice: 'Off means no selected personal facts enter future AI contexts; approved non-AI practices remain available.',
    dataCategories: ['selected profile facts', 'explicit feedback', 'consent metadata'],
    processorCategories: ['PFD server', 'approved AI processor'],
    optional: true
  },
  selected_reflections: {
    title: 'Include selected reflections',
    summary: 'Allows only reflections you explicitly mark to inform personalisation.',
    collectionNotice: 'Private journal text remains excluded by default. This choice never grants blanket journal access.',
    dataCategories: ['user-selected reflection excerpts'],
    processorCategories: ['PFD server', 'approved AI processor when AI consent is also current'],
    optional: true
  },
  push_notifications: {
    title: 'Private reminders',
    summary: 'Allows neutral reminder delivery without relationship detail on the lock screen.',
    collectionNotice: 'Permission is requested later from a clear user gesture. The app remains complete without push.',
    dataCategories: ['Firebase Installation ID', 'timezone', 'quiet hours'],
    processorCategories: ['Firebase Cloud Messaging'],
    optional: true
  },
  offline_persistent_cache: {
    title: 'Remember approved content on this device',
    summary: 'Allows one current Today plan and queued action receipts to remain encrypted on a private, trusted browser.',
    collectionNotice: 'Off keeps PMM memory-only. On uses a non-exportable browser key; sign-out, revocation or untrusting the device purges the vault.',
    dataCategories: ['trusted-device preference', 'encrypted current Today plan', 'encrypted queued action receipts'],
    processorCategories: ['this browser only'],
    optional: true
  }
}

export function applyConsentEvent(current: ConsentState, event: ConsentEvent): ConsentState {
  const existing = current.scopes[event.scope]
  if ((existing?.lastEventId ?? null) !== event.previousEventId) {
    throw new Error('consent_state_conflict')
  }
  return {
    version: current.version + 1,
    scopes: {
      ...current.scopes,
      [event.scope]: {
        granted: event.granted,
        lastEventId: event.eventId,
        decidedAt: event.occurredAt,
        noticeVersion: event.noticeVersion
      }
    }
  }
}

export function isConsentGranted(state: ConsentState, scope: ConsentScope): boolean {
  return state.scopes[scope]?.granted === true
}
